// SubCategories.jsx — the second level of the catalogue.
//
// Same contract as Categories: JSON body, never FormData (multipart is a 415
// here), and the picture goes up separately through /admin/media/subcategories
// which answers with a relative uploads path.
//
// ── THE COLUMNS THAT ARE NOT HERE ──
// The screen this replaces showed eleven columns, four of them Created By,
// Created Date, Updated By, Updated Date. Nobody sorts a catalogue by who
// touched it last; the audit log answers that question properly and this table
// was carrying four columns' worth of width to answer it badly. Added date
// stays because "is this new" is a real question at a glance.

import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";
import { uploadImage } from "../../../api/media";
import {
  fetchSubCategories,
  createSubCategory,
  updateSubCategory,
  deleteSubCategory,
  toggleSubCategoryStatus,
} from "../../../slice/subCategory/subCategorySlice";
import { fetchCategoryDropdown } from "../../../slice/category/categorySlice";
import BannerCropModal from "../banner/BannerCropModal";
import StoreScopePicker, {
  defaultStoreScope,
  storeScopeOf,
} from "../component/StoreScopePicker";
import {
  PageShell,
  DataTable,
  Button,
  Thumb,
  Toggle,
  RowActions,
  Modal,
  Field,
  ImagePicker,
  Combo,
  Pager,
  PlusIcon,
} from "../component/AdminUi";

const BASE_URL = process.env.REACT_APP_API_URL;
const PER_PAGE = 10;

/* Same three rules as the category list and the app's ApiConfig.imageUrl. The
   one difference: a bare filename gets the subcategories/ folder, because that
   is where these live — not every one of them is also in uploads/categories. */
const pictureOf = (row) => {
  const raw = (row?.imageUrl || row?.image || "").trim();
  if (!raw) return "";
  if (raw.startsWith("http://") || raw.startsWith("https://")) return raw;
  const path = raw.replace(/^\/+/, "").replace(/^uploads\//, "");
  return `${BASE_URL}/uploads/${path.includes("/") ? path : `subcategories/${path}`}`;
};

const onDate = (iso) =>
  iso
    ? new Date(iso).toLocaleDateString("en-IN", {
        timeZone: "Asia/Kolkata",
        day: "2-digit",
        month: "short",
        year: "numeric",
      })
    : "—";

export default function SubCategories() {
  const dispatch = useDispatch();
  const { subCategories, loading, error, pagination } = useSelector(
    (s) => s.subCategory
  );
  const { dropdownCategories } = useSelector((s) => s.category);

  const [query, setQuery] = useState("");
  const [page, setPage] = useState(1);

  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [name, setName] = useState("");
  const [categoryId, setCategoryId] = useState("");
  const [scope, setScope] = useState(defaultStoreScope());
  const [picked, setPicked] = useState(null);
  const [preview, setPreview] = useState(null);
  const [cropping, setCropping] = useState(null);
  const [saving, setSaving] = useState(false);
  const [problem, setProblem] = useState("");

  useEffect(() => {
    dispatch(fetchSubCategories({ page, limit: PER_PAGE, search: query }));
  }, [dispatch, page, query]);

  useEffect(() => {
    dispatch(fetchCategoryDropdown());
  }, [dispatch]);

  const categories = dropdownCategories || [];
  const categoryName = (id) =>
    categories.find((c) => String(c.id) === String(id))?.name || "—";

  /* ── the form ──────────────────────────────────────────────────────────── */

  const openNew = () => {
    setEditing(null);
    setName("");
    setCategoryId("");
    setScope(defaultStoreScope());
    setPicked(null);
    setPreview(null);
    setProblem("");
    setOpen(true);
  };

  const openEdit = (row) => {
    setEditing(row);
    setName(row.name || "");
    setCategoryId(row.categoryId || "");
    setScope(storeScopeOf(row));
    setPreview(pictureOf(row) || null);
    setPicked(null);
    setProblem("");
    setOpen(true);
  };

  const close = () => {
    setOpen(false);
    setEditing(null);
    setName("");
    setCategoryId("");
    setScope(defaultStoreScope());
    setPicked(null);
    setPreview(null);
    setProblem("");
  };

  const cropped = (file) => {
    setPicked(file);
    setPreview(URL.createObjectURL(file));
    setCropping(null);
  };

  const save = async () => {
    if (!name.trim()) return setProblem("Give the sub category a name.");
    if (!categoryId) return setProblem("Choose the category it sits under.");
    if (!scope.availableInAllStores && scope.stores.length === 0)
      return setProblem("Choose at least one store, or make it estate-wide.");

    setSaving(true);
    setProblem("");

    const body = { name: name.trim(), categoryId };

    try {
      if (picked) body.image = await uploadImage(picked, "subcategories");

      if (editing) {
        await dispatch(
          updateSubCategory({ id: editing.id, formData: body })
        ).unwrap();
        toast.success("Sub category updated");
        dispatch(fetchSubCategories({ page, limit: PER_PAGE, search: query }));
      } else {
        await dispatch(createSubCategory(body)).unwrap();
        toast.success("Sub category added");
        setPage(1);
        dispatch(fetchSubCategories({ page: 1, limit: PER_PAGE, search: query }));
      }
      close();
    } catch (err) {
      const message = err || "That did not save.";
      setProblem(message);
      toast.error(message);
    } finally {
      setSaving(false);
    }
  };

  const remove = async (row) => {
    if (!window.confirm(`Delete "${row.name}"?`)) return;
    try {
      await dispatch(deleteSubCategory(row.id)).unwrap();
      dispatch(fetchSubCategories({ page, limit: PER_PAGE, search: query }));
      toast.success("Sub category deleted");
    } catch (err) {
      toast.error(err || "Could not delete it.");
    }
  };

  const rows = subCategories || [];

  /* ── render ────────────────────────────────────────────────────────────── */

  return (
    <PageShell
      title="Sub categories"
      count={
        loading && !rows.length
          ? "Loading…"
          : pagination?.total != null
          ? `${pagination.total} in the catalogue`
          : `${rows.length} shown`
      }
      search={{
        value: query,
        onChange: (v) => {
          setQuery(v);
          setPage(1);
        },
        placeholder: "Search sub categories",
      }}
      actions={
        <Button kind="primary" onClick={openNew}>
          <PlusIcon />
          Add sub category
        </Button>
      }
    >
      {cropping ? (
        <BannerCropModal
          file={cropping}
          aspect={1}
          onCancel={() => setCropping(null)}
          onCropped={cropped}
        />
      ) : null}

      <DataTable
        columns={["", "Sub category", "Under", "Shown", "Added", ""]}
        loading={loading}
        error={error}
        isEmpty={!loading && rows.length === 0}
        emptyTitle={query ? "Nothing matches that" : "No sub categories yet"}
        emptyHint={
          query
            ? "Try a shorter search."
            : "Sub categories sit under a category — add the first one."
        }
      >
        {rows.map((row) => (
          <tr key={row.id}>
            <td style={{ width: 62 }}>
              <Thumb src={pictureOf(row)} alt={row.name} />
            </td>
            <td>
              <span className="ui-who__name">{row.name}</span>
            </td>
            <td>{categoryName(row.categoryId)}</td>
            <td>
              <Toggle
                on={row.status !== false}
                onChange={() => dispatch(toggleSubCategoryStatus(row.id))}
                label={
                  row.status !== false
                    ? "Shown in the app — tap to hide"
                    : "Hidden — tap to show"
                }
              />
            </td>
            <td className="ui-num">{onDate(row.createdAt)}</td>
            <td>
              <RowActions
                onEdit={() => openEdit(row)}
                onDelete={() => remove(row)}
              />
            </td>
          </tr>
        ))}
      </DataTable>

      <Pager
        page={page}
        totalPages={pagination?.totalPages}
        total={pagination?.total}
        perPage={PER_PAGE}
        onPage={setPage}
      />

      <Modal
        open={open}
        title={editing ? `Edit ${editing.name}` : "Add sub category"}
        onClose={close}
        footer={
          <>
            <Button onClick={close}>Cancel</Button>
            <Button kind="primary" onClick={save} disabled={saving}>
              {saving ? "Saving…" : editing ? "Save changes" : "Add it"}
            </Button>
          </>
        }
      >
        {problem ? <div className="ui-alert ui-alert--error">{problem}</div> : null}

        <Field label="Name" required>
          <input
            className="ui-input"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="e.g. Wheat flour"
          />
        </Field>

        <Field label="Category" required hint="Which top-level category it belongs to.">
          <Combo
            value={categoryId}
            onChange={setCategoryId}
            options={categories}
            placeholder="Choose a category"
          />
        </Field>

        <Field label="Picture" hint="Cropped square, to match the app's tiles.">
          <ImagePicker
            preview={preview}
            onPick={(file) => setCropping(file)}
            hint="A clear photograph of what is in it"
          />
        </Field>

        <Field label="Stores">
          <StoreScopePicker value={scope} onChange={setScope} />
        </Field>
      </Modal>
    </PageShell>
  );
}
